import { Navigate, Outlet, useLocation } from 'react-router';

import { useCurrentUser, useSessionStatus } from '../hooks/useSession';

import { ROUTES } from '@/app/routes';
import { FullPageSpinner } from '@/components/layouts/FullPageSpinner';

/**
 * Route guard.
 *
 * Warning: `loading` must render a spinner, not redirect — on reload the session
 * is only restored after a network round trip, so redirecting there would
 * bounce an already-authenticated user to the login screen.
 */
export function RequireAuth() {
  const status = useSessionStatus();
  const user = useCurrentUser();
  const location = useLocation();

  if (status === 'loading') return <FullPageSpinner />;

  if (status === 'anonymous') {
    // `state.from` sends the user back where they were headed, not always the dashboard.
    return <Navigate to={ROUTES.login} replace state={{ from: location }} />;
  }

  /*
   * ⚠️ Before the first launch, and before everything else: an account whose
   * address is unconfirmed cannot use the application. The server enforces it
   * (EmailVerifiedGuard, which answers 403 to every other route), so without
   * this the reader would reach a screen that fails to load rather than one
   * that says why.
   *
   * ⚠️ It **returns**, rather than falling through to the checks below. An
   * unconfirmed newcomer satisfies the first-launch condition too, and the two
   * redirects would then bounce off each other forever: the wall sends them to
   * the sequence, the sequence sends them back. Nothing further applies to an
   * account that cannot open the application at all.
   *
   * The pending screen itself is let through, or the redirect would chase its
   * own tail.
   */
  if (user && !user.emailVerified) {
    return location.pathname === ROUTES.verifyEmailPending ? (
      <Outlet />
    ) : (
      <Navigate to={ROUTES.verifyEmailPending} replace />
    );
  }

  /*
   * Warning: the first launch is shown **before** anything else, and only
   * once — the stamp lives on the account, so a second device is not a second
   * first launch. Sending somebody to the dashboard first and opening the
   * sequence over it would show them an empty screen they cannot read yet.
   *
   * The sequence's own route is excluded, or the redirect would chase itself.
   */
  if (user?.onboardedAt === null && location.pathname !== ROUTES.onboarding) {
    return <Navigate to={ROUTES.onboarding} replace />;
  }

  return <Outlet />;
}
